import { ApolloError } from '@apollo/client';
import { remapApolloErrors } from 'helpers/remapApolloErrors';
import { showToast } from 'helpers/showToast';
import {
  UpdateAutoImportInput,
  AutoImportStatus,
  useUpdatePauseSettingsMutation,
  useEditAutoImportMutation
} from 'models/graphql';
import { AppContext } from 'providers/AppProvider';
import React, { useCallback, useContext, useState } from 'react';
import { PauseImporterModalView, PauseImporterModalViewProps } from './PauseImporterModal.view';

export type PauseImporterModalControllerProps = Omit<PauseImporterModalViewProps, 'onConfirmation' | 'loading'> & {
  isImportersPaused: boolean;
};

export const PauseImporterModalController = ({
  onClose,
  importer,
  isImportersPaused
}: PauseImporterModalControllerProps) => {
  const [loading, setLoading] = useState(false);
  const { selectedWorkspaceId } = useContext(AppContext);
  const [updatePauseSetting] = useUpdatePauseSettingsMutation();
  const [editAutoImport] = useEditAutoImportMutation();

  const onConfirmation = useCallback(async () => {
    setLoading(true);
    if (importer) {
      try {
        const input: UpdateAutoImportInput = {
          status: importer.status === AutoImportStatus.PAUSED ? AutoImportStatus.ACTIVE : AutoImportStatus.PAUSED
        };
        await editAutoImport({
          variables: { autoImportId: importer.id, input }
        });

        onClose();
      } catch (e) {
        const error = e as ApolloError;
        const mappedError = remapApolloErrors(error);
        showToast({
          summary: 'Error in pausing the Auto Importer',
          detail: mappedError,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    } else if (selectedWorkspaceId) {
      try {
        updatePauseSetting({
          variables: { input: { pauseAutoImports: !isImportersPaused }, workspaceId: selectedWorkspaceId }
        });

        onClose();
      } catch (e) {
        const error = e as ApolloError;
        const mappedError = remapApolloErrors(error);
        showToast({
          summary: 'Error in pausing the Auto Importers',
          detail: mappedError,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  }, [importer, editAutoImport, onClose, selectedWorkspaceId, isImportersPaused, updatePauseSetting]);

  const props: PauseImporterModalViewProps = { onClose, onConfirmation, loading, importer, isImportersPaused };
  return <PauseImporterModalView {...props}></PauseImporterModalView>;
};
